import React, { useEffect, useState } from 'react';
import { delete_beneficiary_by_id, get_beneficiary } from '../../helper/Api';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { FaEdit, FaEye, FaEyeSlash } from 'react-icons/fa';
import { AiOutlineEye } from 'react-icons/ai';
import {
    DELETE_BENEFICIARY_START,
    DELETE_BENEFICIARY_SUCCESS,
    GET_BENEFICIARY_FAIL,
    GET_BENEFICIARY_START,
    GET_BENEFICIARY_SUCCESS,
} from '../../RTK/Slices/BeneficiarySlice';
import { MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Spinner from '../Tools/spinner';
import './ManageBeneficiary.css';

const getApprovalBadgeClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'bg-warning-20';
    }
    const val = String(approval);
    if (val === '1') return 'bg-success-20';
    if (val === '0' || val === '2') return 'bg-danger-20';
    return 'bg-warning-20';
};

const getApprovalIconClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'icon-warning_amber text-warning-100';
    }
    const val = String(approval);
    if (val === '1') return 'icon-verified text-success-100';
    if (val === '0' || val === '2') return 'icon-warning_amber text-danger-100';
    return 'icon-warning_amber text-warning-100';
};

const ApprovalStatusCell = ({ l2Approval, l3Approval }) => (
    <div className="ben-approval-status-cell">
        <span className={`ben-approval-status-badge ${getApprovalBadgeClass(l2Approval)}`}>
            L2 <i className={`${getApprovalIconClass(l2Approval)} fs-16px`} />
        </span>
        <span className={`ben-approval-status-badge ${getApprovalBadgeClass(l3Approval)}`}>
            L3 <i className={`${getApprovalIconClass(l3Approval)} fs-16px`} />
        </span>
    </div>
);

const getActiveAccountsCount = (row) => {
    const accounts = Array.isArray(row?.beneficiary_accounts) ? row.beneficiary_accounts : [];
    return accounts.filter((account) => String(account?.status) === '1').length;
};

const maskPan = (pan) => {
    const raw = String(pan || '').trim().toUpperCase();
    if (!raw) return '';
    if (raw.length <= 4) return raw;
    return `XXXXX${raw.slice(-4)}`;
};

const getBeneficiaryStatusBadgeClass = (status) => {
    const key = String(status || '').trim().toLowerCase();
    if (!key) return 'ben-beneficiary-status-badge--default';
    if (key.includes('individual')) return 'ben-beneficiary-status-badge--individual';
    if (key.includes('proprietor')) return 'ben-beneficiary-status-badge--proprietor';
    if (key.includes('partnership')) return 'ben-beneficiary-status-badge--partnership';
    if (key.includes('llp')) return 'ben-beneficiary-status-badge--llp';
    if (key.includes('private') || key.includes('limited') || key.includes('pvt')) {
        return 'ben-beneficiary-status-badge--company';
    }
    if (key.includes('huf')) return 'ben-beneficiary-status-badge--huf';
    if (key.includes('trust')) return 'ben-beneficiary-status-badge--trust';
    if (key.includes('society')) return 'ben-beneficiary-status-badge--society';
    if (key.includes('government') || key.includes('govt')) return 'ben-beneficiary-status-badge--government';
    return 'ben-beneficiary-status-badge--other';
};

const PanCell = ({ row, isRevealed, onToggle }) => {
    const holder = String(row.pan_holder_name || '').trim();
    const panRaw = String(row.pan_no || '').trim().toUpperCase();
    const displayPan = isRevealed ? panRaw : maskPan(panRaw);

    if (!holder && !panRaw) return '-';

    return (
        <div className="ben-pan-cell">
            {holder ? <span className="ben-pan-holder">{holder}</span> : null}
            {panRaw ? (
                <div className="ben-pan-row">
                    <span className={`ben-pan-value${isRevealed ? ' ben-pan-value--revealed' : ''}`}>
                        {displayPan}
                    </span>
                    <Tooltip title={isRevealed ? 'Hide PAN' : 'Show full PAN'} arrow placement="top">
                        <button
                            type="button"
                            className="ben-pan-toggle-btn"
                            onClick={(event) => {
                                event.stopPropagation();
                                onToggle();
                            }}
                            aria-label={isRevealed ? 'Hide PAN' : 'Show full PAN'}
                        >
                            {isRevealed ? <FaEyeSlash size={11} /> : <FaEye size={11} />}
                        </button>
                    </Tooltip>
                </div>
            ) : null}
        </div>
    );
};

const ManageBeneficiary = (listType) => {
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const { authData } = useSelector((state) => state.authData);
    const dispatch = useDispatch();
    const location = useLocation();

    const [beneficiariesList, setBeneficiaries] = useState([]);
    const [accountsModalOpen, setAccountsModalOpen] = useState(false);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [selectedBeneficiaryName, setSelectedBeneficiaryName] = useState('');
    const [revealedPanIds, setRevealedPanIds] = useState(() => new Set());

    const isAgency =
        location.pathname.includes('agency') || location.search.includes('agency');

    const getBeneficiaryType = () => (isAgency ? 'agency' : undefined);

    const openAccountsModal = (row) => {
        const accounts = Array.isArray(row?.beneficiary_accounts) ? row.beneficiary_accounts : [];
        setSelectedAccounts(accounts);
        setSelectedBeneficiaryName(row?.company_name || 'Beneficiary');
        setAccountsModalOpen(true);
    };

    const closeAccountsModal = () => {
        setAccountsModalOpen(false);
        setSelectedAccounts([]);
        setSelectedBeneficiaryName('');
    };

    const togglePanReveal = (id) => {
        setRevealedPanIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const columns = [
        {
            name: 'Actions',
            cell: (row) => {
                const canEdit =
                    authData.user.user_type === 'l1' &&
                    (row.l2_status === '2' || row.l3_status === '2');
                const canDelete =
                    authData.user.user_type === 'l3' &&
                    row.l2_status === '1' &&
                    row.l3_status === '1';

                return (
                    <div className="ben-actions">
                        <Tooltip title="View Details" arrow placement="top">
                            <span className="ben-action-tooltip-wrap">
                                <Link
                                    to={`/beneficiary/${row.id}`}
                                    className="ben-action-btn ben-action-btn--view"
                                >
                                    <AiOutlineEye size={16} />
                                </Link>
                            </span>
                        </Tooltip>
                        {canEdit ? (
                            <Tooltip title="Edit" arrow placement="top">
                                <span className="ben-action-tooltip-wrap">
                                    <Link
                                        to={`/edit-beneficiary/${row.id}`}
                                        className="ben-action-btn ben-action-btn--edit"
                                    >
                                        <FaEdit size={15} />
                                    </Link>
                                </span>
                            </Tooltip>
                        ) : null}
                        {canDelete ? (
                            <Tooltip title="Delete" arrow placement="top">
                                <span className="ben-action-tooltip-wrap">
                                    <button
                                        type="button"
                                        className="ben-action-btn ben-action-btn--delete"
                                        onClick={() => delete_beneficiary(row.id)}
                                        aria-label={`Delete beneficiary ${row.company_name || row.id}`}
                                    >
                                        <MdDelete size={16} />
                                    </button>
                                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                );
            },
            center: true,
            width: '100px',
            grow: 0,
        },
        {
            name: 'Status',
            cell: (row) => (
                <ApprovalStatusCell l2Approval={row.l2_status} l3Approval={row.l3_status} />
            ),
            center: true,
            minWidth: '160px',
        },
        {
            name: 'Beneficiary Name',
            selector: (row) => row.company_name || '-',
            sortable: true,
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Active Accounts',
            cell: (row) => {
                const count = getActiveAccountsCount(row);
                if (count === 0) {
                    return <span className="ben-accounts-count-badge ben-accounts-count-badge--empty">0</span>;
                }
                return (
                    <button
                        type="button"
                        className="ben-accounts-count-badge"
                        onClick={() => openAccountsModal(row)}
                    >
                        {count}
                    </button>
                );
            },
            center: true,
            minWidth: '130px',
        },
        {
            name: 'PAN',
            cell: (row) => (
                <PanCell
                    row={row}
                    isRevealed={revealedPanIds.has(row.id)}
                    onToggle={() => togglePanReveal(row.id)}
                />
            ),
            wrap: true,
            minWidth: '180px',
        },
        {
            name: 'Contact Person',
            selector: (row) => row.contact_person || '-',
            sortable: true,
            wrap: true,
            minWidth: '160px',
        },
        {
            name: 'Email',
            selector: (row) => row.email || '-',
            sortable: true,
            wrap: true,
            minWidth: '180px',
        },
        {
            name: 'Mobile',
            selector: (row) => row.mobile || '-',
            sortable: true,
            wrap: true,
            width: '130px',
        },
        {
            name: 'Beneficiary Status',
            cell: (row) => {
                const label = String(row.beneficiar_status || row.beneficiary_status || '').trim();
                if (!label) return '-';
                return (
                    <span
                        className={`ben-beneficiary-status-badge ${getBeneficiaryStatusBadgeClass(label)}`}
                    >
                        {label}
                    </span>
                );
            },
            center: true,
            minWidth: '150px',
        },
    ];

    const fetchBeneficiaries = async (type) => {
        setLoading(true);
        dispatch(GET_BENEFICIARY_START());
        try {
            const beneficiaries = await get_beneficiary({
                from_date: '',
                to_date: '',
                search_str: '',
                department_id: listType?.departmentId || authData.user.department_id || '',
                type,
            });
            if (beneficiaries.data.status && beneficiaries.data.list) {
                if (listType.listType === 'list') {
                    dispatch(GET_BENEFICIARY_SUCCESS(beneficiaries.data.list));
                    setBeneficiaries(beneficiaries.data.list);
                } else if (listType.listType === 'actionList' && authData.user.user_type === 'l2') {
                    const list = beneficiaries.data.list.filter((el) => el.l2_status === '0');
                    dispatch(GET_BENEFICIARY_SUCCESS(list));
                    setBeneficiaries(list);
                } else if (listType.listType === 'actionList' && authData.user.user_type === 'l3') {
                    const list = beneficiaries.data.list.filter((el) => el.l3_status === '0');
                    dispatch(GET_BENEFICIARY_SUCCESS(list));
                    setBeneficiaries(list);
                }
            } else {
                dispatch(GET_BENEFICIARY_FAIL(beneficiaries.data.message));
            }
        } catch (error) {
            dispatch(GET_BENEFICIARY_FAIL('something went wrong'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBeneficiaries(getBeneficiaryType());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listType?.departmentId, listType.listType, location.pathname, location.search]);

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
    };

    const delete_beneficiary = async (beneficiary_id) => {
        if (!beneficiary_id) {
            toast.error('beneficiary id required.', { position: toast.POSITION.TOP_CENTER });
            return;
        }
        const result = window.confirm('Are you sure you want to delete the beneficiary?');
        if (!result) return;

        try {
            dispatch(DELETE_BENEFICIARY_START());
            const formData = { beneficiary_id };
            const { data } = await delete_beneficiary_by_id(formData);
            if (data.status) {
                dispatch(DELETE_BENEFICIARY_SUCCESS({ id: beneficiary_id }));
                fetchBeneficiaries(getBeneficiaryType());
                toast.success(data.message, { position: toast.POSITION.TOP_CENTER });
            } else {
                toast.error(data.message, { position: toast.POSITION.TOP_CENTER });
            }
        } catch (error) {
            toast.error('getting error while delete user.', { position: toast.POSITION.TOP_CENTER });
        }
    };

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
    };

    return (
        <div className="beneficiary-datatable-root">
            {loading && (
                <div>
                    <Spinner />
                </div>
            )}
            <Dialog
                open={accountsModalOpen}
                onClose={closeAccountsModal}
                maxWidth="md"
                fullWidth
                aria-labelledby="ben-accounts-modal-title"
            >
                <DialogTitle
                    id="ben-accounts-modal-title"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pr: 1,
                    }}
                >
                    <span>Bank Accounts — {selectedBeneficiaryName}</span>
                    <IconButton
                        onClick={closeAccountsModal}
                        aria-label="Close"
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedAccounts.length === 0 ? (
                        <p className="mb-0 text-muted">No accounts found.</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="ben-accounts-modal-table">
                                <thead>
                                    <tr>
                                        <th>Project</th>
                                        <th>Beneficiary Name</th>
                                        <th>Bank</th>
                                        <th>Branch</th>
                                        <th>Account No</th>
                                        <th>IFSC</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedAccounts.map((account) => (
                                        <tr key={account.id || `${account.account_no}-${account.ifsc_code}`}>
                                            <td>{account.project_name || '-'}</td>
                                            <td>{account.beneficiary_name || '-'}</td>
                                            <td>{account.bank_name || '-'}</td>
                                            <td>{account.branch_name || '-'}</td>
                                            <td>{account.account_no || '-'}</td>
                                            <td>{account.ifsc_code || '-'}</td>
                                            <td>
                                                <span
                                                    className={`ben-account-status-badge ${
                                                        String(account.status) === '1'
                                                            ? 'ben-account-status-badge--active'
                                                            : 'ben-account-status-badge--inactive'
                                                    }`}
                                                >
                                                    {String(account.status) === '1' ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <DataTable
                className="dataTables_wrapper"
                customStyles={customStyles}
                columns={columns}
                data={beneficiariesList}
                fixedHeader
                fixedHeaderScrollHeight="600px"
                pagination
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </div>
    );
};

export default ManageBeneficiary;
